/* eslint-disable @typescript-eslint/no-unused-vars */
import 'animate.css';

import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';

import ExportIcon from '../../assets/img/export.svg';
import { StopsCreateMenu } from '../../components/menus/StopsCreateMenu';
import { TableColumnsSortConfig } from '../../context/shared/tableColumns';
import { useRetrieveOrganization } from '../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveSupportDocs } from '../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../hooks/user/useRetrieveUserCredentials';
import Button from '../../react-components/Button';
import Text from '../../react-components/Text';
import { StopsActionsMenu } from './StopsActionsMenu';
import { StopsTableColumnKeys } from './context/tableColumns';
import { useSearchStops } from './hooks/useSearchStops/useSearchStops';
import { useSetSelectedStops } from './hooks/useSetSelectedStops';
import { SearchFilters } from './search-filters/SearchFilters';

import More3Points from '../../assets/svg/More3Points';
import { HeaderContent } from '../../components/HeaderContent';
import { StopRightPanel } from '../planner/shared/right-panel/stop-right-panel/StopRightPanel';
import { ExportStopsDialog } from './ExportStopsDialog';
import { PlannerStopsUnderGorilla } from './PlannerStopsUnderGorilla';
import { useStopsContext } from './context/context';
import { PlannerStopsList } from './planner-stops-list';
import { PlannerStopsListColumnsButton } from './planner-stops-list/PlannerStopsListColumnsButton';

export const SEARCH_LIMIT = 20;

export const PlannerStopsContent = () => {
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: user } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: organization } = useRetrieveOrganization();
  const { data: activeProject } = useRetrieveActiveProject();
  const { detailType } = useRetrieveRightPanelInfo();
  const { selectedStops } = useSetSelectedStops();
  const [isExportStopsDialogOpen, setIsExportStopsDialogOpen] = useState<boolean>(false);
  const { stopState } = useStopsContext();

  const [searchParameters, setSearchParameters] = useState<{ limit: number; offset: number }>({
    offset: 0,
    limit: SEARCH_LIMIT,
  });

  const [sortStopsInfo, setSortStopsInfo] = useState<TableColumnsSortConfig<StopsTableColumnKeys>>({
    orderBy: `created_at`,
    order: `desc`,
  });

  const { run: searchStops } = useSearchStops({
    project: activeProject,
    offset: searchParameters.offset,
    limit: searchParameters.limit,
    sortBy: sortStopsInfo.orderBy,
    sortDirection: sortStopsInfo.order,
  });

  if (!activeProject || !organization || !stopState) {
    return null;
  }

  if (!userCredentials?.subscription.test(`planner.stops`)) {
    return <PlannerStopsUnderGorilla />;
  }

  return (
    <>
      <Grid
        container
        style={{
          height: `100%`,
          width: `100%`,
          zIndex: 2,
          backgroundColor: `white`,
          display: `flex`,
          flexDirection: `row`,
          minWidth: `1080px`,
        }}
        wrap="nowrap"
      >
        <div
          style={{
            height: `100%`,
            width: `100%`,
          }}
        >
          <Grid container direction="column" wrap="nowrap" style={{ height: `100%` }}>
            <Grid
              container
              direction="row"
              wrap="nowrap"
              alignItems="center"
              style={{
                padding: `11px 20px`,
                backgroundColor: RoutalPalette.neutral02n,
                borderBottom: `1px solid ${RoutalPalette.neutral10}`,
              }}
            >
              <HeaderContent
                titleIntlId="planner.stops.title"
                article={{
                  intlId: `planner.stops.header.article`,
                  link: retrieveDoc({ supportPage: `createStopsVideo`, language: user?.language.value }),
                }}
              />

              <Grid container item justifyContent="flex-end">
                {activeProject ? (
                  <StopsCreateMenu
                    id="list-stops-create-actions"
                    intlId="planner.stops.add_new"
                    style={{
                      backgroundColor: RoutalPalette.primary40,
                      justifyContent: `space-between`,
                      borderRadius: `10px`,
                    }}
                    hideSeparator
                    containerStyle={{
                      width: `auto`,
                      paddingLeft: `14px`,
                      paddingRight: `8px`,
                      whiteSpace: `nowrap`,
                    }}
                  />
                ) : null}
              </Grid>
            </Grid>

            <Grid container direction="row" wrap="nowrap" style={{ height: `calc(100% - 70px)` }}>
              <Grid container direction="column" wrap="nowrap">
                <Grid container direction="column" style={{ padding: `12px 20px 0px 20px` }}>
                  <Grid container item justifyContent="space-between" alignItems="center" direction="row" wrap="nowrap">
                    <Grid container item direction="row">
                      <SearchFilters language={user?.language} userCredentials={userCredentials} />
                    </Grid>

                    <Grid
                      item
                      container
                      direction="row"
                      justifyContent="flex-end"
                      wrap="nowrap"
                      style={{ maxWidth: `400px` }}
                    >
                      <PlannerStopsListColumnsButton id="stops-configurable-table-columns" />
                      <HSpacer small />
                      <Button
                        styleType="secondary"
                        disabled={selectedStops.length === 0}
                        onClick={() => setIsExportStopsDialogOpen(true)}
                        style={{
                          height: `35px`,
                          width: `35px`,
                          padding: 0,
                          margin: 0,
                          borderRadius: `5px`,
                        }}
                      >
                        <img alt="" height="20px" src={ExportIcon} draggable="false" />
                      </Button>
                    </Grid>
                  </Grid>

                  <VSpacer small />

                  <Grid container item alignItems="center" direction="row">
                    {selectedStops.length === 0 ? (
                      <Text intlId="planner.stops.num_stops" intlValues={{ total: stopState.pagination.total }} />
                    ) : (
                      <Text
                        intlId="planner.stops.num_selected_stops"
                        intlValues={{ num: selectedStops.length, total: stopState.pagination.total }}
                      />
                    )}

                    <HSpacer medium />

                    <Grid item>
                      {stopState.flat.filter((stop) => selectedStops.includes(stop.id)).length > 0 ? (
                        <StopsActionsMenu
                          type="popover"
                          stops={stopState.flat.filter((stop) => selectedStops.includes(stop.id))}
                          popoverProps={{
                            hideArrow: false,
                            style: {
                              justifyContent: `space-between`,
                            },
                            containerStyle: {
                              width: `240px`,
                            },
                            content: (
                              <Text
                                variant="body"
                                intlId="plans.plan.data.services.header.actions"
                                style={{ cursor: `pointer` }}
                              />
                            ),
                          }}
                          afterDeleteStops={(deletedStopsIds: string[]) => {
                            searchStops();
                          }}
                        />
                      ) : null}
                    </Grid>
                  </Grid>
                </Grid>

                <PlannerStopsList
                  searchParameters={searchParameters}
                  sortStopsInfo={sortStopsInfo}
                  setSearchParameters={setSearchParameters}
                  setSortStopsInfo={setSortStopsInfo}
                />
              </Grid>
              {detailType === `stop` ? (
                <StopRightPanel
                  selectedStopsIds={selectedStops}
                  validatePlan={false}
                  stopActionsMenu={
                    <StopsActionsMenu
                      type="popover"
                      stops={[]}
                      showViewDetails={false}
                      popoverProps={{
                        hideArrow: true,
                        content: <More3Points />,
                        style: {
                          color: RoutalPalette.secondary60,
                          padding: 0,
                          margin: 0,
                          border: `unset`,
                          backgroundColor: RoutalPalette.neutral00,
                        },
                      }}
                    />
                  }
                />
              ) : null}
            </Grid>
          </Grid>
        </div>
      </Grid>

      <ExportStopsDialog
        open={isExportStopsDialogOpen}
        onCloseDialog={() => setIsExportStopsDialogOpen(false)}
        selectedStopIds={selectedStops}
      />
    </>
  );
};
